/**
 * HYBRID SEARCH HISTORY SYSTEM
 *
 * Combines localStorage (fast, guest-friendly) with backend sync (persistent, cross-device)
 *
 * SAVE STRATEGY (High-Quality Data Only):
 * ✅ Save on Enter key press - User confirms search
 * ✅ Save on property card click - User found what they wanted
 * ❌ DON'T save on typing - Prevents partial/junk data ("m", "mi", "mia")
 * ❌ DON'T save empty searches - Only meaningful searches
 *
 * ARCHITECTURE:
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Guest User Flow                                             │
 * ├─────────────────────────────────────────────────────────────┤
 * │ Search → localStorage only                                  │
 * │ Fast, instant access, works offline                         │
 * │ Data persists in browser (max 20 items)                     │
 * └─────────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ Logged-In User Flow                                         │
 * ├─────────────────────────────────────────────────────────────┤
 * │ 1. Search → localStorage (instant save)                     │
 * │ 2. Background sync → backend (every 5 min or on login)      │
 * │ 3. On login → merge backend + local history                 │
 * │ 4. Cross-device → history syncs across devices              │
 * └─────────────────────────────────────────────────────────────┘
 *
 * OPTIMIZATION FEATURES:
 * ✅ In-memory cache (5 min TTL) - Fastest access
 * ✅ localStorage cache - Fast, persistent
 * ✅ Debounced saves (1s) - Prevent excessive writes
 * ✅ Duplicate detection - No redundant entries
 * ✅ Smart syncing - Only sync unsynced items
 * ✅ Auto-trimming - Keep max 20 items
 *
 * CACHE HIERARCHY (Fastest → Slowest):
 * 1. In-Memory Cache (0ms) - RAM
 * 2. localStorage (1-5ms) - Browser storage
 * 3. Backend API (100-300ms) - Server database
 */

import { PropertyFilters } from "@/services/types/property";

export interface SearchHistoryItem {
  id: string;
  filters: PropertyFilters;
  searchedAt: string;
  resultCount: number;
  synced?: boolean; // Track if synced to backend
}

const STORAGE_KEY = "propertySearchHistory";
const MAX_HISTORY_ITEMS = 20;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
const LAST_SYNC_KEY = "searchHistoryLastSync";

// In-memory cache for fast access
let memoryCache: SearchHistoryItem[] | null = null;
let cacheTimestamp: number | null = null;

// Debounce timer for saving to prevent excessive writes
let saveDebounceTimer: NodeJS.Timeout | null = null;
const SAVE_DEBOUNCE_MS = 1000; // Wait 1 second after last filter change

/**
 * Get all search history (with in-memory cache)
 */
export const getSearchHistory = (): SearchHistoryItem[] => {
  try {
    // Check in-memory cache first (fastest)
    const now = Date.now();
    if (
      memoryCache &&
      cacheTimestamp &&
      now - cacheTimestamp < CACHE_DURATION
    ) {
      return memoryCache;
    }

    // Fall back to localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    const history = stored ? JSON.parse(stored) : [];

    // Update cache
    memoryCache = history;
    cacheTimestamp = now;

    return history;
  } catch (error) {
    console.error("Error reading search history:", error);
    return [];
  }
};

/**
 * Invalidate the in-memory cache
 */
const invalidateCache = (): void => {
  memoryCache = null;
  cacheTimestamp = null;
};

/**
 * Add a new search to history (optimized with debouncing and caching)
 */
export const addToSearchHistory = (
  filters: PropertyFilters,
  resultCount: number
): void => {
  // Clear previous debounce timer
  if (saveDebounceTimer) {
    clearTimeout(saveDebounceTimer);
  }

  // Debounce the save operation
  saveDebounceTimer = setTimeout(() => {
    try {
      // Don't save if no meaningful filters (empty search)
      if (!hasActiveFilters(filters)) {
        return;
      }

      const history = getSearchHistory();

      // Check if this exact search already exists (avoid duplicates)
      const existingIndex = history.findIndex((item) =>
        areFiltersEqual(item.filters, filters)
      );

      const newItem: SearchHistoryItem = {
        id: Date.now().toString(),
        filters: { ...filters },
        searchedAt: new Date().toISOString(),
        resultCount,
        synced: false, // Not yet synced to backend
      };

      // Remove existing duplicate if found
      if (existingIndex !== -1) {
        history.splice(existingIndex, 1);
      }

      // Add to beginning of array (most recent first)
      history.unshift(newItem);

      // Keep only the most recent items
      const trimmedHistory = history.slice(0, MAX_HISTORY_ITEMS);

      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedHistory));

      // Invalidate cache to force refresh
      invalidateCache();
    } catch (error) {
      console.error("Error saving search history:", error);
    }
  }, SAVE_DEBOUNCE_MS);
};

/**
 * Clear all search history
 */
export const clearSearchHistory = async (): Promise<void> => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    invalidateCache();

    // Also clear from backend if user is logged in
    try {
      const { clearAllSearchHistory } = await import(
        "@/services/api/searchHistory"
      );
      await clearAllSearchHistory();
    } catch (error) {
      // Silently fail if not logged in or backend error
    }
  } catch (error) {
    console.error("Error clearing search history:", error);
  }
};

/**
 * Remove a specific item from history
 */
export const removeFromSearchHistory = async (id: string): Promise<void> => {
  try {
    const history = getSearchHistory();
    const filtered = history.filter((item) => item.id !== id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
    invalidateCache();

    // Also delete from backend if user is logged in
    try {
      const { deleteSearchHistoryItem } = await import(
        "@/services/api/searchHistory"
      );
      await deleteSearchHistoryItem(id);
    } catch (error) {
      // Silently fail if not logged in or backend error
    }
  } catch (error) {
    console.error("Error removing from search history:", error);
  }
};

/**
 * Check if filters have any active values
 */
const hasActiveFilters = (filters: PropertyFilters): boolean => {
  return !!(
    filters.city ||
    filters.state ||
    filters.minPrice ||
    filters.maxPrice ||
    filters.bedrooms !== undefined ||
    filters.bathrooms !== undefined ||
    filters.propertyType ||
    filters.listingType
  );
};

/**
 * Compare two filter objects for equality
 */
const areFiltersEqual = (
  filters1: PropertyFilters,
  filters2: PropertyFilters
): boolean => {
  return (
    filters1.city === filters2.city &&
    filters1.state === filters2.state &&
    filters1.minPrice === filters2.minPrice &&
    filters1.maxPrice === filters2.maxPrice &&
    filters1.bedrooms === filters2.bedrooms &&
    filters1.bathrooms === filters2.bathrooms &&
    filters1.propertyType === filters2.propertyType &&
    filters1.listingType === filters2.listingType
  );
};

/**
 * Format filters into a readable string
 */
export const formatSearchDescription = (filters: PropertyFilters): string => {
  const parts: string[] = [];

  if (filters.city) {
    parts.push(filters.city);
  }
  if (filters.state && filters.state !== filters.city) {
    parts.push(filters.state);
  }
  if (filters.bedrooms !== undefined) {
    parts.push(`${filters.bedrooms === 5 ? "5+" : filters.bedrooms} bed`);
  }
  if (filters.bathrooms !== undefined) {
    parts.push(`${filters.bathrooms === 5 ? "5+" : filters.bathrooms} bath`);
  }
  if (filters.minPrice || filters.maxPrice) {
    const min = filters.minPrice
      ? `$${(filters.minPrice / 1000).toFixed(0)}k`
      : "";
    const max = filters.maxPrice
      ? `$${(filters.maxPrice / 1000).toFixed(0)}k`
      : "";
    if (min && max) {
      parts.push(`${min}-${max}`);
    } else if (min) {
      parts.push(`${min}+`);
    } else if (max) {
      parts.push(`under ${max}`);
    }
  }
  if (filters.propertyType) {
    parts.push(filters.propertyType);
  }
  if (filters.listingType) {
    parts.push(filters.listingType);
  }

  return parts.length > 0 ? parts.join(" • ") : "All properties";
};

/**
 * Get relative time string (e.g., "2 hours ago", "Yesterday")
 */
export const getRelativeTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60)
    return `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
  if (diffHours < 24)
    return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30)
    return `${Math.floor(diffDays / 7)} ${
      Math.floor(diffDays / 7) === 1 ? "week" : "weeks"
    } ago`;
  return date.toLocaleDateString();
};

/**
 * Sync local search history to backend (for logged-in users)
 * This merges localStorage history with backend history
 */
export const syncSearchHistoryToBackend = async (
  _userId: string
): Promise<void> => {
  try {
    const localHistory = getSearchHistory();

    // Debug: Show all items and their sync status

    const unsyncedItems = localHistory.filter((item) => !item.synced);

    if (unsyncedItems.length === 0) {
      return;
    }

    // Import API service
    const { syncSearchHistoryToBackend: syncAPI } = await import(
      "@/services/api/searchHistory"
    );

    // API call to sync history
    await syncAPI(unsyncedItems);

    // Mark items as synced in localStorage
    const updatedHistory = localHistory.map((item) => ({
      ...item,
      synced: true,
    }));
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedHistory));
    invalidateCache();

    // Update last sync timestamp
    localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
  } catch (error) {
    console.error("Error syncing search history:", error);
    // Don't throw - fail gracefully, keep local history
  }
};

/**
 * Load search history from backend (on login)
 * This merges backend history with local history
 */
export const loadSearchHistoryFromBackend = async (
  _userId: string
): Promise<void> => {
  try {
    // Import API service
    const { getSearchHistoryFromBackend } = await import(
      "@/services/api/searchHistory"
    );

    // API call to get backend history
    const backendHistory: SearchHistoryItem[] =
      await getSearchHistoryFromBackend(20);

    // Get local history
    const localHistory = getSearchHistory();

    // Merge: Backend history + unique local items
    const mergedHistory = [...backendHistory];

    // Add local items that don't exist in backend
    localHistory.forEach((localItem) => {
      const existsInBackend = backendHistory.some((backendItem) =>
        areFiltersEqual(localItem.filters, backendItem.filters)
      );
      if (!existsInBackend) {
        mergedHistory.push({ ...localItem, synced: false });
      }
    });

    // Sort by date (most recent first)
    mergedHistory.sort(
      (a, b) =>
        new Date(b.searchedAt).getTime() - new Date(a.searchedAt).getTime()
    );

    // Keep max items
    const trimmedHistory = mergedHistory.slice(0, MAX_HISTORY_ITEMS);

    // Mark ONLY backend items as synced, keep local items as unsynced
    const finalHistory = trimmedHistory.map((item: any) => ({
      ...item,
      id: item.id || item._id || Date.now().toString(), // Ensure id exists
      synced: !!item._id, // Only synced if it has backend _id
    }));

    localStorage.setItem(STORAGE_KEY, JSON.stringify(finalHistory));
    invalidateCache();
  } catch (error) {
    console.error("Error loading search history from backend:", error);
    // Don't throw - fail gracefully, keep local history
  }
};

/**
 * Get last sync timestamp
 */
export const getLastSyncTime = (): Date | null => {
  try {
    const lastSync = localStorage.getItem(LAST_SYNC_KEY);
    return lastSync ? new Date(lastSync) : null;
  } catch (error) {
    return null;
  }
};

/**
 * Check if sync is needed
 * Returns true if:
 * 1. Never synced before
 * 2. At least 5 minutes passed AND there are unsynced items (for recent changes)
 * 3. At least 1 hour passed (for regular periodic sync)
 */
export const shouldSync = (): boolean => {
  const lastSync = getLastSyncTime();
  if (!lastSync) return true;

  const timeSinceSync = Date.now() - lastSync.getTime();
  const minutesSinceSync = timeSinceSync / (1000 * 60);
  const hoursSinceSync = timeSinceSync / (1000 * 60 * 60);

  // Regular periodic sync every hour
  if (hoursSinceSync >= 1) return true;

  // If at least 5 minutes passed, check for unsynced items
  if (minutesSinceSync >= 5) {
    const history = getSearchHistory();
    const hasUnsyncedItems = history.some((item) => !item.synced);
    if (hasUnsyncedItems) {
      return true;
    }
  }

  return false;
};
